import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { VividWordmark } from '../primitives.jsx';
import { Button, Spinner } from '../components/v24/index.jsx';
import { getPendingHandoff } from '../sso.js';

// How long the destination gets before the screen says it is not answering.
const STALL_MS = 10 * 1000;

// The cross-origin sign-in used to end in a bare navigation: nothing on
// screen while the workspace loaded, and a blank page if it never did
// (AUTH-507). This page shows progress in a status region, goes to the
// destination, and after ten seconds with no navigation says so, with a
// retry and a way back.
export default function HandoffPage() {
  const navigate = useNavigate();
  const target = useMemo(() => getPendingHandoff(), []);
  const [stalled, setStalled] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!target) {
      // Reloaded or opened directly: nothing to hand off.
      navigate('/login', { replace: true });
      return undefined;
    }
    const timer = setTimeout(() => setStalled(true), STALL_MS);
    window.location.href = target.url;
    return () => clearTimeout(timer);
  }, [target, attempt, navigate]);

  if (!target) return null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
      <VividWordmark />
      {stalled ? (
        <div role="alert" className="mt-8 max-w-[420px] text-center">
          <p className="text-[15px] text-ink">
            Your workspace is not responding. Try again, or sign in again.
          </p>
          <div className="mt-5 flex items-center justify-center gap-4">
            <Button onClick={() => { setStalled(false); setAttempt((n) => n + 1); }}>Try again</Button>
            <Link to="/login" className="text-[13px] font-medium text-ink hover:underline">
              Back to sign in
            </Link>
          </div>
        </div>
      ) : (
        <div role="status" className="mt-8 flex items-center gap-3 text-[15px] text-stone-600">
          <span aria-hidden="true"><Spinner size={20} label="Taking you to your workspace" /></span>
          <span>Taking you to your workspace...</span>
        </div>
      )}
    </div>
  );
}
